import { notificationFieldText, useNotificationContentLanguage } from "../../i18n/notificationContentPresentation";
import { useDashboardLanguage } from "../../i18n/useDashboardLanguage";
import { useShellText } from "../../i18n/useShellText";
import { useMemo } from "react";
import type { NotificationItem } from "../../hooks/useNotificationsFeed";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { MarkdownContent } from "../ui/MarkdownContent";
import { getNotificationTypeLabel, typeColor } from "./notificationsUtils";

type Props = {
  items: NotificationItem[];
  loading?: boolean;
  error?: string;
  maxItems?: number;
  onRefresh?: () => void;
  onMarkRead?: (ids?: number[]) => void;
  onDismiss?: (ids: number[]) => void;
  onItemClick?: (item: NotificationItem) => void;
};

export const NotificationsInboxSummary = ({
  items,
  loading = false,
  error,
  maxItems,
  onRefresh,
  onMarkRead,
  onDismiss,
  onItemClick,
}: Props) => {
  useNotificationContentLanguage();
  const { text: ui, t } = useShellText();
  const { displayedLocale } = useDashboardLanguage();
  const visibleItems = useMemo(() => (maxItems ? items.slice(0, maxItems) : items), [items, maxItems]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">{ui("Loading notifications...")}</div>;
  }

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/40 p-3 text-sm text-destructive-text">
        <span>{t($ => $.notificationError)}</span>
        {onRefresh && (
          <Button size="sm" variant="ghost" onClick={() => void onRefresh()}>
            {ui("Retry")}</Button>
        )}
      </div>
    );
  }

  if (!visibleItems.length) {
    return (
      <div className="rounded-md border border-border/50 bg-muted/40 p-3 text-sm text-muted-foreground">
        {ui("No notifications yet.")}</div>
    );
  }

  return (
    <div className="space-y-3">
      {visibleItems.map((item) => (
        <div
          key={item.id}
          className="rounded-lg border border-border/60 bg-card/80 p-4 shadow-sm transition-colors hover:bg-muted/20"
          onClick={() => onItemClick?.(item)}
          role={onItemClick ? "button" : undefined}
        >
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted/70">
                <Icon icon="lucide:bell" className="h-4 w-4 text-primary-text" />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  {!item.read && (
                    <span
                      className="inline-flex h-2 w-2 shrink-0 rounded-full bg-primary"
                      aria-label={ui("Unread notification")}
                    />
                  )}
                  <span className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">
                    {notificationFieldText(item, "title") }
                  </span>
                </div>
                {item.body && (
                  <div className="line-clamp-3">
                    <MarkdownContent content={notificationFieldText(item, "body") } compact />
                  </div>
                )}
                {item.actionHref && item.actionLabel && (
                  <a
                    href={item.actionHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary-text underline underline-offset-2"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span>{notificationFieldText(item, "actionLabel") }</span>
                    <Icon icon="lucide:arrow-up-right" className="h-3 w-3" aria-hidden="true" />
                    <span className="sr-only">{ui("(opens in new tab)")}</span>
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <div className="flex items-center gap-2">
                <Badge
                  size="sm"
                  variant={typeColor[item.type]}
                  className="px-1 py-[3px] text-[9px] leading-none whitespace-nowrap"
                >
                  {ui(getNotificationTypeLabel(item.type))}
                </Badge>
                <span>{new Date(item.createdAt * 1000).toLocaleString(displayedLocale)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>{item.read ? ui("Read") : ui("Unread")}</span>
                {!item.read && onMarkRead && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-auto whitespace-nowrap px-1.5 py-1 text-[11px] font-normal text-muted-foreground hover:text-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      void onMarkRead([item.id]);
                    }}
                  >
                    {ui("Mark read")}
                  </Button>
                )}
                {onDismiss && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-auto whitespace-nowrap px-1.5 py-1 text-muted-foreground hover:text-destructive"
                    onClick={(event) => {
                      event.stopPropagation();
                      void onDismiss([item.id]);
                    }}
                    aria-label={ui("Dismiss notification")}
                  >
                    <Icon icon="lucide:x" className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
